import { useEffect, useMemo, useState } from 'react';
import { User, Mail, Phone, Lock, Save, Edit2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';
import { bookingService } from '../../services/bookingService';
import { UI, buttonStyle, fieldStyles, ui } from '../../utils/ui';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [totalBookings, setTotalBookings] = useState(0);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user) setFormData({ name: user.name || '', phone: user.phone || '' });
    bookingService.getMyBookings()
      .then((res) => setTotalBookings(res.data.bookings?.length || 0))
      .catch(() => {});
  }, [user]);

  const joinedYear = useMemo(() => new Date(user?.created_at || Date.now()).getFullYear(), [user?.created_at]);

  const spinnerStyle = (color = '#ffffff') => ({
    width: 14,
    height: 14,
    border: `2px solid rgba(255,255,255,0.35)`,
    borderTopColor: color,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await userService.updateMyProfile({ name: formData.name, phone: formData.phone });
      updateUser({ name: formData.name, phone: formData.phone });
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pwData.newPassword !== pwData.confirmPassword) { toast.error('New passwords do not match.'); return; }
    if (pwData.newPassword.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    setChangingPw(true);
    try {
      await authService.changePassword(pwData.currentPassword, pwData.newPassword);
      setPwData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setChangingPw(false);
    }
  };

  const inputRowStyle = (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 14,
    border: `1.5px solid ${UI.colors.border}`,
    backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
  });

  const inputCoreStyle = {
    ...fieldStyles.input,
    border: 'none',
    padding: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    flex: 1,
  };

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 18 };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };
  const sectionTitleStyle = { fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, color: UI.colors.text, margin: 0 };

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div>
        <h1 style={titleStyle}>My Profile</h1>
        <p style={subtitleStyle}>Manage your account information</p>
      </div>

      {/* Profile Card */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          {/* Avatar Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 18, marginBottom: 18, borderBottom: `1px solid ${UI.colors.border}`, flexWrap: 'wrap' }}>
            <div style={{ width: 92, height: 92, borderRadius: 18, backgroundColor: 'rgba(22,163,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontFamily: UI.font.heading, fontSize: 44, fontWeight: 900, color: UI.colors.primary }}>
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontFamily: UI.font.heading, fontSize: 24, fontWeight: 900, color: UI.colors.text, margin: 0, lineHeight: 1.1 }}>{user?.name}</h2>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 10px', borderRadius: 9999, backgroundColor: UI.colors.primary, color: '#ffffff', fontSize: 11, fontWeight: 900, textTransform: 'capitalize' }}>
                  {user?.user_type}
                </span>
                <span style={{ fontSize: 13, color: UI.colors.muted, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</span>
              </div>
            </div>
          </div>

          {/* Form Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <h3 style={sectionTitleStyle}>Personal Information</h3>
            {!isEditing ? (
              <button
                type="button"
                style={{ ...buttonStyle('outline'), padding: '10px 12px', borderRadius: 14 }}
                onClick={() => setIsEditing(true)}
              >
                <Edit2 size={16} /> Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={{ ...buttonStyle('ghost'), padding: '10px 12px', borderRadius: 14 }}
                  onClick={() => setIsEditing(false)}
                >
                  <X size={16} /> Cancel
                </button>
                <button
                  type="button"
                  style={{ ...buttonStyle('primary'), padding: '10px 12px', borderRadius: 14, opacity: saving ? 0.75 : 1 }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <span style={spinnerStyle()} /> : <Save size={16} />}
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            {/* Name */}
            <div>
              <label style={fieldStyles.label}>Full Name</label>
              <div className="input-row" style={inputRowStyle(!isEditing)}>
                <User size={16} color="#9ca3af" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!isEditing}
                  style={{ ...inputCoreStyle, cursor: isEditing ? 'text' : 'not-allowed' }}
                />
              </div>
            </div>

            {/* Email (read-only) */}
            <div>
              <label style={fieldStyles.label}>Email Address</label>
              <div className="input-row" style={inputRowStyle(true)}>
                <Mail size={16} color="#9ca3af" />
                <input type="email" value={user?.email || ''} disabled style={{ ...inputCoreStyle, cursor: 'not-allowed' }} />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label style={fieldStyles.label}>Phone Number</label>
              <div className="input-row" style={inputRowStyle(!isEditing)}>
                <Phone size={16} color="#9ca3af" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!isEditing}
                  style={{ ...inputCoreStyle, cursor: isEditing ? 'text' : 'not-allowed' }}
                />
              </div>
            </div>

            {/* Account Type */}
            <div>
              <label style={fieldStyles.label}>Account Type</label>
              <div className="input-row" style={inputRowStyle(true)}>
                <User size={16} color="#9ca3af" />
                <input type="text" value={user?.user_type || ''} disabled style={{ ...inputCoreStyle, textTransform: 'capitalize', cursor: 'not-allowed' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {[
          { label: 'Total Bookings', value: totalBookings, color: UI.colors.primary, bg: 'rgba(22,163,74,0.12)' },
          { label: 'Member Since', value: joinedYear, color: UI.colors.info, bg: 'rgba(37,99,235,0.12)' },
          { label: 'Account Type', value: user?.user_type || '-', color: '#7c3aed', bg: 'rgba(124,58,237,0.12)' },
        ].map((s) => (
          <div key={s.label} style={{ ...ui.card, padding: 18, textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: s.color, textTransform: 'capitalize' }}>{s.value}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Change Password */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          <h3 style={sectionTitleStyle}>Change Password</h3>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>Keep your account secure with a strong password</p>

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, maxWidth: 760 }}>
            {[
              { key: 'currentPassword', label: 'Current Password', placeholder: 'Enter current password' },
              { key: 'newPassword', label: 'New Password', placeholder: 'Min. 8 characters' },
              { key: 'confirmPassword', label: 'Confirm New Password', placeholder: 'Repeat new password' },
            ].map((f) => (
              <div key={f.key}>
                <label style={fieldStyles.label}>{f.label}</label>
                <div className="input-row" style={inputRowStyle(false)}>
                  <Lock size={16} color="#9ca3af" />
                  <input
                    type="password"
                    placeholder={f.placeholder}
                    value={pwData[f.key]}
                    onChange={(e) => setPwData({ ...pwData, [f.key]: e.target.value })}
                    style={inputCoreStyle}
                  />
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                onClick={handlePasswordChange}
                disabled={changingPw}
                style={{ ...buttonStyle('primary'), padding: '12px 16px', borderRadius: 14, opacity: changingPw ? 0.75 : 1 }}
              >
                {changingPw ? <span style={spinnerStyle()} /> : <Lock size={16} />}
                {changingPw ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

